import { humanize } from '@ideon-api/ideon-ui'
import universe from './entitlement-universe.json'

// Types mirror `plan_entitlements` in app/schemas/account_entitlements.schema.json.
// Keys stay snake_case: audience and product line names are wire values.

export const QUARTERS = ['Q1', 'Q2', 'Q3', 'Q4'] as const
export type Quarter = (typeof QUARTERS)[number]

export type Audience = keyof PlanYear
export const QUARTER_AUDIENCES = universe.quarter_audiences as readonly Audience[]
export const ANNUAL_AUDIENCES = universe.annual_audiences as readonly Audience[]

export interface PlanEntry {
  product_line: string
  states: string[]
}
export type QuarterlyPlans = Partial<Record<Quarter, PlanEntry[]>>
export interface PlanYear {
  small_group: QuarterlyPlans
  individual: PlanEntry[]
  level_funded: PlanEntry[]
  medicare_advantage: PlanEntry[]
  large_group_custom: QuarterlyPlans
}
export type PlanEntitlements = Record<string, PlanYear>

export interface CoverageLine {
  productLine: string
  audience: Audience
}
// A null audience stands for every audience: an ancillary line with no data at all.
export interface NotLicensedLine {
  productLine: string
  audience: Audience | null
}
// Quarter keys for a quarterly audience, a single `Annual` key otherwise.
export type Periods = Partial<Record<Quarter | 'Annual', string[]>>

const AUDIENCES: readonly Audience[] = [...QUARTER_AUDIENCES, ...ANNUAL_AUDIENCES]
// Core medical lines show for every set, even when empty.
const MEDICAL_LINES: CoverageLine[] = AUDIENCES.map((audience) => ({ productLine: 'medical', audience }))

const isQuarterly = (audience: Audience) => (QUARTER_AUDIENCES as readonly string[]).includes(audience)

const isPlanEntry = (e: unknown): e is PlanEntry =>
  !!e &&
  typeof e === 'object' &&
  typeof (e as PlanEntry).product_line === 'string' &&
  Array.isArray((e as PlanEntry).states)

// Well-formed entries only, so a malformed element degrades instead of throwing.
const asEntries = (raw: unknown): PlanEntry[] => (Array.isArray(raw) ? raw.filter(isPlanEntry) : [])

// Entries for one audience in one year; [] for a missing or malformed shape.
function entriesFor(plans: PlanEntitlements | null | undefined, year: string, audience: Audience): PlanEntry[] {
  const raw: unknown = plans?.[year]?.[audience]
  if (isQuarterly(audience)) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return []
    return QUARTERS.flatMap((q) => asEntries((raw as QuarterlyPlans)[q]))
  }
  return asEntries(raw)
}

const statesOf = (entries: PlanEntry[], productLine?: string) => [
  ...new Set(entries.filter((e) => !productLine || e.product_line === productLine).flatMap((e) => e.states)),
]

const yearsOf = (plans: PlanEntitlements | null | undefined) =>
  Object.keys(plans ?? {}).sort((a, b) => Number(b) - Number(a))

export function quarterStates(
  plans: PlanEntitlements | null | undefined,
  year: string,
  productLine: string,
  audience: Audience
): Periods {
  if (!isQuarterly(audience)) return { Annual: statesOf(entriesFor(plans, year, audience), productLine) }
  const raw: unknown = plans?.[year]?.[audience]
  const byQuarter = raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as QuarterlyPlans) : {}
  return Object.fromEntries(QUARTERS.map((q) => [q, statesOf(asEntries(byQuarter[q]), productLine)]))
}

// Total lines = licensed.length + notLicensed.length; an all-audiences entry counts once.
export function coverageLines(
  plans: PlanEntitlements | null | undefined,
  year: string
): { licensed: CoverageLine[]; notLicensed: NotLicensedLine[] } {
  const seen = new Map<string, CoverageLine>(MEDICAL_LINES.map((l) => [`${l.productLine}.${l.audience}`, l]))
  for (const y of yearsOf(plans)) {
    for (const audience of AUDIENCES) {
      for (const e of entriesFor(plans, y, audience)) {
        seen.set(`${e.product_line}.${audience}`, { productLine: e.product_line, audience })
      }
    }
  }

  const licensed: CoverageLine[] = []
  const notLicensed: NotLicensedLine[] = []
  for (const line of seen.values()) {
    const has = statesOf(entriesFor(plans, year, line.audience), line.productLine).length > 0
    ;(has ? licensed : notLicensed).push(line)
  }
  const withData = new Set([...seen.values()].map((l) => l.productLine))
  for (const productLine of universe.ancillary_product_lines) {
    if (!withData.has(productLine)) notLicensed.push({ productLine, audience: null })
  }
  return { licensed, notLicensed }
}

export function latestYearWithData(plans: PlanEntitlements | null | undefined): string | null {
  return yearsOf(plans).find((y) => AUDIENCES.some((a) => statesOf(entriesFor(plans, y, a)).length > 0)) ?? null
}

// Years (newest first) where the pair has an entry, even one with no states.
export function history(
  plans: PlanEntitlements | null | undefined,
  productLine: string,
  audience: Audience
): Array<{ year: string; periods: Periods }> {
  return yearsOf(plans)
    .filter((y) => entriesFor(plans, y, audience).some((e) => e.product_line === productLine))
    .map((y) => ({ year: y, periods: quarterStates(plans, y, productLine, audience) }))
}

export const audienceLabel = (audience: Audience) => (audience === 'level_funded' ? 'Level-Funded' : humanize(audience))
